import { PageContext } from "../service/PageContext";
import { DomUtil } from "../util/DomUtil";
import { WyanokeUtil } from "../util/WyanokeUtil";
import { MyCmeService } from "../service/MyCmeService";
import { SavedPageService } from "../service/SavedPageService";
import { IMyCmeModel } from "../models/MyCme/IMyCmeModel";
import { ISavedPageGetResponse } from "../models/SavedPage/ISavedPageGetResponse";
import { UrlUtil } from "../util/UrlUtil";
import { IMyHealioCmeModel } from "../models/MyCme/IMyHealioCmeModel";
import Mustache from "mustache";

export class BindMyCme {
  private readonly _myCmeService: MyCmeService | null;
  private readonly _savedPageService: SavedPageService | null;
  private readonly _creditBreakdownTemplate: string;
  private _isBound = false;

  public static UpdateCreditsEvent = "BindMyCme.UpdateCreditsEvent"
  public MyCmeJson: IMyHealioCmeModel | null = null;
  public MyCmeItemTemplate: string | null = null;
  public MyResourceItemTemplate: string | null = null;

  constructor(
    private readonly _element: HTMLElement,
    context: PageContext | null
  ) {
    this._myCmeService = context ? context.myCmeService : null;
    this._savedPageService = context ? context.savedPageService : null;
    const template =
      _element.querySelector("[data-mycme-template='creditBreakdown']");

    this._creditBreakdownTemplate = template ? template.innerHTML : "";

    if (template) {
      template.remove();
    }

    if (!this._myCmeService) {
      console.warn("No MyCME service available.");
    }

    if (this._savedPageService) {
      window.addEventListener(
        SavedPageService.SavedPageIncrementEvent,
        () => this.updateSavedCount(1));

      window.addEventListener(
        SavedPageService.SavedPageDecrementEvent,
        () => this.updateSavedCount(-1));

      window.addEventListener(
        BindMyCme.UpdateCreditsEvent,
        e => this.updateCreditsCount(e as CustomEvent<number>)
      )
    } else {
      console.warn("No Saved Page service available");
    }
  }

  private static bindValues(root: HTMLElement, data: object): void {
    const query = DomUtil.queryAll.bind(null, root);
    for (const [key, value] of Object.entries(data)) {
      query(`[data-mycme='${key}']`)
        .forEach(el => el.textContent = String(value));
    }
  }

  private static formatCredits(credits: number, fixedDecimalPlaces = 2): string {
    // Justification: Need to have 2 decimal places for CME credits
    // tslint:disable-next-line: no-magic-numbers
    return credits.toFixed(fixedDecimalPlaces);
  }

  private static bindMyScrollToTargets() {
    const queryString = UrlUtil.getQueryString()
    const sectionContains = queryString.get("sectioncontains")
    const expandAccordion = queryString.get("expand")

    if (sectionContains === undefined || sectionContains[0]?.length < 1) {
      return;
    }

    const scrollToSection = $(`[id*="${sectionContains}"]:visible`)
    if (scrollToSection.length) {
      if (expandAccordion) {
        let collapseToggleElement = $(`#${expandAccordion} [data-toggle]`)
        if (collapseToggleElement.length && collapseToggleElement[0]?.classList?.contains("collapsed")) {
          collapseToggleElement[0].click()
        }
        setTimeout(function () {
          let accordionElement = $(`#${expandAccordion}:visible`)
          if (accordionElement.length) {
            accordionElement[0].scrollIntoView({ behavior: "smooth" });
          } else {
            scrollToSection[0].scrollIntoView({ behavior: "smooth" });
          }
        }, 1000);
      }
    }
  }

  public async bind(loadMyCmeJson = false): Promise<boolean> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile || profile.isLoggedIn === "False") {
      return false;
    }

    const contactId = profile.contactid || null;
    if (contactId === null || contactId === "") {
      return false;
    }

    this.MyCmeItemTemplate = document.querySelector('#MyCMEItemTemplate')?.innerHTML ?? null;
    this.MyResourceItemTemplate = document.querySelector('#MyCMEResourceItemTemplate')?.innerHTML ?? null;
    const activitySiteName = (this._element.dataset.siteName || "").toLowerCase();
    const myCmeService = this._myCmeService;
    const savedPageService = this._savedPageService;
    const myCmePromise = myCmeService ? myCmeService.getMyCme() : null;
    const myHealioCmePromise = myCmeService && loadMyCmeJson ? myCmeService.getMyCmeJson() : null;

    const savedPagePromise =
      savedPageService ? savedPageService.list("cme") : null;

    const results = await Promise.all([myCmePromise, savedPagePromise, myHealioCmePromise]);
    const myCmeInfo = results[0];
    const savedPages = results[1];
    this.MyCmeJson = results[2];

    const firstName = profile.firstname || "";
    const lastName = profile.lastname || "";
    const username = firstName + " " + lastName;
    const profession = (profile.profession || "").trim();
    const specialty = (profile.specialty || "").trim();
    const professionAndSpecialty =
      [profession, specialty].filter(Boolean).join(", ");

    const totalCreditsRaw = myCmeInfo ? myCmeInfo.totalCredits : 0;
    const completed = myCmeInfo ? myCmeInfo.completed : 0;
    const inProgress = myCmeInfo ? myCmeInfo.inProgress : 0;
    const saved = savedPages ? savedPages.filter(function (i: ISavedPageGetResponse): boolean {
      return i.site?.toLocaleLowerCase() === activitySiteName && new Date(i.expires) > new Date();
    }).length : 0;


    const totalCreditsElement = this._element.querySelector<HTMLSpanElement>("[data-mycme='totalCredits']")
    const decimalPlaces = totalCreditsElement && totalCreditsElement.dataset.decimalPlaces ? Number(totalCreditsElement.dataset.decimalPlaces) : 2
    const totalCredits = BindMyCme.formatCredits(totalCreditsRaw, decimalPlaces);

    BindMyCme.bindValues(
      this._element,
      {
        completed,
        inProgress,
        profession,
        professionAndSpecialty,
        saved,
        specialty,
        totalCredits,
        username
      });

    BindMyCme.bindMyScrollToTargets()

    if (myCmeInfo) {
      this.bindCreditBreakdown(myCmeInfo);
    }

    this._isBound = true;

    return true;
  }

  private bindCreditBreakdown(myCmeInfo: IMyCmeModel): void {
    const targets = DomUtil.queryAll(
      this._element,
      "[data-mycme='creditBreakdown']");

    if (!targets.length) {
      return;
    }

    const template = this._creditBreakdownTemplate;
    if (!template || myCmeInfo.creditBreakdown.length === 0) {
      targets.forEach(target => target.innerHTML = "");

      return;
    }

    const frag = document.createDocumentFragment();
    const node = frag.appendChild(document.createElement("div"));

    const sorted = myCmeInfo.creditBreakdown.slice(0).sort((a, b) => {
      if (!a) {
        return !b ? 0 : 1;
      }

      if (!b) {
        return -1;
      }

      const aType = (a.creditType || "").toLowerCase();
      const bType = (b.creditType || "").toLowerCase();

      if (aType < bType) {
        return -1;
      }

      if (aType > bType) {
        return 1;
      }

      return 0;
    });

    targets.forEach(target => {
      sorted.forEach(c => {
        const credits = BindMyCme.formatCredits(c.credits);
        node.innerHTML = this._creditBreakdownTemplate;
        BindMyCme.bindValues(node, { credits, creditType: c.creditType });
        while (node.firstChild) {
          target.appendChild(node.firstChild);
        }
      });
    });

    this.showCreditBreakdownLabel(myCmeInfo);
  }

  private showCreditBreakdownLabel(myCmeInfo: IMyCmeModel): void {
    const labelTarget = DomUtil.queryAll(
      this._element,
      "[data-mycme='creditBreakdownLabel']");

    if (labelTarget.length) {
      if (myCmeInfo.completed > 0) {
        labelTarget.forEach(
          target => {
            target.classList.add("d-xl-block");
          });
      }
    }
  }

  private updateSavedCount(update: number): void {
    console.log("Update saved", update);
    if (!this._isBound) {
      return;
    }

    DomUtil.queryAll(this._element, "[data-mycme='saved']").forEach(el => {
      const raw = (el.textContent || "").trim();
      const parsed = parseInt(raw, 10);
      if (isNaN(parsed)) {
        return;
      }

      const updated = Math.max(0, parsed + update);
      el.textContent = updated.toString();
    });
  }

  private updateCreditsCount(update: CustomEvent<number>): void {
    const totalCreditsElement = this._element.querySelector<HTMLSpanElement>("[data-mycme='totalCredits']")
    if (!totalCreditsElement) {
      return;
    }

    if (!update.detail) {
      return;
    }

    let updateCredits = Number(update.detail);
    if (!updateCredits) {
      return;
    }

    let currentCredits = Number(totalCreditsElement.textContent);
    let newCredits = currentCredits + updateCredits;

    const decimalPlaces = totalCreditsElement && totalCreditsElement.dataset.decimalPlaces ? Number(totalCreditsElement.dataset.decimalPlaces) : 2
    const totalCredits = BindMyCme.formatCredits(newCredits, decimalPlaces);

    totalCreditsElement.textContent = totalCredits;
  }

  public renderCmeItemsFromTemplate(activities: any): string {
    let renderedItems = "";
    // tslint:disable-next-line: no-unsafe-any
    if (this.MyCmeItemTemplate) {
      for (let activity of activities) {
        const renderedTemplate = Mustache.render(this.MyCmeItemTemplate, activity);
        renderedItems += renderedTemplate
      };
    }

    return renderedItems;
  }

  public renderResourceItemsFromTemplate(resources: any): string {
    let renderedItems = "";
    // tslint:disable-next-line: no-unsafe-any
    if (this.MyResourceItemTemplate) {
      for (let resource of resources) {
        const renderedTemplate = Mustache.render(this.MyResourceItemTemplate, resource);
        renderedItems += renderedTemplate
      };
    }

    return renderedItems;
  }
}